'use client';
import { DownloadIcon, PlusIcon } from '@c/icons';
import { PencilIcon } from '@heroicons/react/20/solid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@ui/Button';
import { PaginatorClient } from '@ui/Paginator';
import SafeImage from '@ui/SafeImage';
import Toggle from '@ui/Toggle';
import { exportListings } from '@util/firestore/listing';
import {
  bumpProduct,
  getProductViews,
  getSellerListingsPaginated,
  updateProductByKeys,
} from '@util/firestore/products';
import { instantSearchClient } from '@util/getTypesense';
import useDebounce from '@util/hooks/useDebounce';
import { formatCurrency } from '@util/index';
import { logError } from '@util/logError';
import { getProductSlug } from '@util/urlHelpers';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { deleteField } from 'firebase/firestore';
import {
  isVehicleCategory,
  ProductDocument,
  productSchema,
} from 'models/product';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { IoSearchOutline } from 'react-icons/io5';
import {
  Configure,
  InstantSearch,
  useHits,
  useInstantSearch,
  useSearchBox,
} from 'react-instantsearch';
import DeleteListingModal from './components/DeleteListingModal';
import PriceDropModal from './components/PriceDropModal';
import SmartPricingModal from './components/SmartPricingModal';
import SmartPricingOffModal from './components/SmartPricingOffModal';
import EmptyBox from '@i/empty-box.svg';
import { ChevronsUpDown, Eye, Heart } from 'lucide-react';
import { SortModal } from './components/SortModal';
import { PromoteModal } from './components/PromoteModal';

export type Availability =
  | 'onSale'
  | 'inStock'
  | 'draft'
  | 'sold'
  | 'notSold'
  | 'paymentFailed'
  | 'all';

export const ListingsPage = () => {
  const { userDoc } = useAuth();

  const queryClient = useQueryClient();
  const searchParams = useSearchParams();
  const [search, setSearch] = useState(searchParams?.get('q') ?? '');
  const [_, setQSearch] = useQueryState('q', {
    history: 'replace',
  });
  const debouncedSearchTerm = useDebounce(search, 500);
  useEffect(() => {
    setQSearch(debouncedSearchTerm);
  }, [debouncedSearchTerm, setQSearch]);

  const [smartPricingModalOpen, setSmartPricingModalOpen] = useState(false);
  const [smartPricingOffModalOpen, setSmartPricingOffModalOpen] =
    useState(false);

  const [deleteListingModalOpen, setDeleteListingModalOpen] = useState(false);
  const [priceDropModalOpen, setPriceDropModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] =
    useState<ProductDocument | null>(null);
  const [deletedProducts, setDeletedProducts] = useState<string[]>([]);
  const { showSuccessToast, showErrorToast } = useToastContext();

  const [sortModalIsOpen, toggleSortModalIsOpen] = useReducer(
    (prev) => !prev,
    false
  );

  const [promoteModalIsOpen, setPromoteModalIsOpen] = useState(false);

  const [sort, setSort] = useQueryState('sort', {
    history: 'replace',
    ...parseAsString.withDefault('Most Recent'),
  });

  const [page, setPage] = useQueryState('page', {
    history: 'replace',
    ...parseAsInteger.withDefault(1),
  });
  const [availability, setAvailability] = useQueryState('availability', {
    history: 'replace',
    ...parseAsString.withDefault('all'),
  });
  const [auction, setAuction] = useQueryState('auction', {
    history: 'replace',
    ...parseAsString.withDefault('all'),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['listings', page, availability, userDoc?.uid, auction],
    queryFn: () =>
      getSellerListingsPaginated(
        userDoc?.uid ?? '',
        userDoc?.roles?.admin ?? false,
        page,
        availability as Availability,
        auction
      ),
  });

  function getFilters() {
    let filterString = '';
    if (!(userDoc?.roles?.admin || userDoc?.roles?.support)) {
      filterString += `seller_id:=${userDoc?.uid} && `;
    }
    if (availability === 'onSale') {
      filterString += `on_sale:true && `;
    }
    if (filterString.endsWith('&& ')) {
      filterString = filterString.slice(0, -4);
    }

    return filterString;
  }

  const products = useMemo(() => {
    const _products = (data?.results ?? []).filter(
      (product) => !deletedProducts.some((id) => id === product.id)
    );

    const sorted = [..._products].sort((a, b) => {
      switch (sort) {
        case 'Most Recent':
          return (b.created ?? 0) - (a.created ?? 0);
        case 'Alphabetically (A-Z)':
          return a.title.localeCompare(b.title);
        case 'Highest Price':
          return b.price - a.price;
        case 'Lowest Price':
          return a.price - b.price;
        default:
          return 0;
      }
    });

    return sorted;
  }, [data?.results, deletedProducts, sort]);

  const index = 'products_alias';

  const updateFloorPrice = async (
    floorPrice: number | undefined,
    product: ProductDocument
  ) => {
    try {
      const keys = productSchema.pick({
        floor_price: true,
      });
      if (floorPrice) {
        const res = keys.safeParse({ floor_price: floorPrice });
        if (!res.success) {
          logError(res.error.message);
          throw new Error('Unable to update product. Please contact support.');
        }
      }
      const updates = {
        floor_price: floorPrice ? floorPrice : deleteField(),
        date_updated: Date.now(),
      };
      await updateProductByKeys(product.id, updates);
      setSmartPricingModalOpen(false);
      setSmartPricingOffModalOpen(false);

      queryClient.invalidateQueries({
        queryKey: ['listings'],
      });
      showSuccessToast('Floor price updated');
    } catch (e) {
      showErrorToast((e as Error).message);
    }
  };

  const onConfirmPriceDrop = async (
    newPrice: number,
    product: ProductDocument
  ) => {
    try {
      if (product.shipment?.package?.insured_value?.amount)
        product.shipment.package.insured_value.amount = newPrice;

      const keys = productSchema.pick({
        price: true,
        on_sale: true,
        shipment: true,
        date_updated: true,
      });
      const updates = {
        price: newPrice,
        on_sale: true,
        floor_price: deleteField(),
        ...(product.shipment ? { shipment: product.shipment } : {}),
        date_updated: Date.now(),
      };
      const res = keys.safeParse(updates);
      if (res.success) {
        await updateProductByKeys(product.id, {
          ...updates,
        });
        queryClient.invalidateQueries({
          queryKey: ['listings'],
        });
        showSuccessToast('Price updated');
      } else {
        logError(res.error);
        showErrorToast('Error updating product. Please contact support.');
      }
    } catch (e) {
      showErrorToast((e as Error).message);
    }
  };

  const filterPills: { label: string; value: Availability }[] = [
    { label: 'Listing', value: 'all' },
    { label: 'On Sale', value: 'onSale' },
    { label: 'Sold', value: 'sold' },
    { label: 'Drafts', value: 'draft' },
  ];

  return (
    <div className="relative flex flex-col">
      <div className="z-50">
        {!!selectedProduct && (
          <>
            <SmartPricingModal
              setPromoteModalIsOpen={setPromoteModalIsOpen}
              open={smartPricingModalOpen}
              setOpen={setSmartPricingModalOpen}
              product={selectedProduct}
              updateSmartPricing={updateFloorPrice}
              key={selectedProduct.id}
            />
            <SmartPricingOffModal
              open={smartPricingOffModalOpen}
              setOpen={setSmartPricingOffModalOpen}
              product={selectedProduct}
              updateFloorPrice={updateFloorPrice}
              key={selectedProduct.id + 'smartPricingOffModal'}
            />
            <DeleteListingModal
              open={deleteListingModalOpen}
              setOpen={setDeleteListingModalOpen}
              product={selectedProduct}
              key={selectedProduct.id + 'deleteListingModal'}
              onDelete={() => {
                queryClient.invalidateQueries({
                  queryKey: ['listings'],
                });
                // add the deleted product to the deleted products state for client side optimistic update
                setDeletedProducts([
                  ...deletedProducts,
                  selectedProduct.id ?? '',
                ]);
              }}
            />

            <PriceDropModal
              setPromoteModalIsOpen={setPromoteModalIsOpen}
              open={priceDropModalOpen}
              setOpen={setPriceDropModalOpen}
              product={selectedProduct}
              updatePriceDrop={onConfirmPriceDrop}
            />

            <PromoteModal
              open={promoteModalIsOpen}
              setOpen={setPromoteModalIsOpen}
              setSmartPricingModalOpen={setSmartPricingModalOpen}
              setPriceDropModalOpen={setPriceDropModalOpen}
            />
          </>
        )}
        <SortModal
          open={sortModalIsOpen}
          toggle={toggleSortModalIsOpen}
          onSelectSort={setSort}
          sortSelected={sort}
        />
      </div>
      <div className="mb-8 flex flex-col justify-between gap-y-6 pt-8 lg:flex-row lg:gap-y-0 lg:pt-0">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-heading-h4 font-bold">Listings</h1>
          <Link
            href={'/create-listing'}
            className="flex shrink-0 items-center gap-3 rounded-full bg-brand-secondary px-8 py-4 text-body-md font-semibold text-white transition-all duration-150 hover:bg-brand-secondary-darker lg:hidden"
          >
            <PlusIcon className="size-8" />
            Add listing
          </Link>
        </div>
        <div className="flex flex-col gap-[1.6rem] sm:flex-row">
          <div className="flex w-full gap-5">
            <div className="flex flex-1 items-center gap-5 rounded-full border border-[#d9d9d9] bg-white px-6 py-4 font-medium text-black">
              <IoSearchOutline className="size-10" />
              <input
                type="text"
                className="h-full w-full border-none text-body-md font-medium outline-none "
                placeholder="Search your listing"
                value={search ?? ''}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
              />
            </div>
            <Link
              href={'/create-listing'}
              className="hidden shrink-0 items-center gap-3 rounded-full bg-brand-secondary px-8 py-4 text-body-md font-semibold text-white transition-all duration-150 hover:bg-brand-secondary-darker lg:flex"
            >
              <PlusIcon className="size-8" />
              Add listing
            </Link>
            <button
              type="button"
              onClick={toggleSortModalIsOpen}
              className="flex size-[44px] items-center justify-center rounded-full bg-light-grey lg:hidden"
            >
              <ChevronsUpDown className="size-10" />
            </button>
          </div>
          {/* <Link
            href={'/dashboard/integrations'}
            className="flex items-center justify-center gap-x-4 rounded-2xl bg-[#EDEDED] px-8 py-4 font-medium text-black transition-all duration-150 hover:bg-[#d9d9d9] lg:justify-start"
          >
            <FiRefreshCw /> Import Listings
          </Link> */}
          {userDoc?.username === 'MotorPsychoSport' && (
            <Button
              type="tertiary"
              text="Export Listings to CSV"
              width="small"
              leadingIcon={<DownloadIcon />}
              onClick={async () => {
                if (userDoc) {
                  await exportListings({ userDoc });
                  queryClient.invalidateQueries({
                    queryKey: ['authUser'],
                  });
                }
              }}
            />
          )}
        </div>
      </div>
      <div className="mb-16 flex w-full gap-4 overflow-x-auto text-nowrap text-body-sm font-medium scrollbar-none">
        {filterPills.map((pill) => {
          const active = availability === pill.value;
          return (
            <button
              key={pill.value}
              onClick={() => setAvailability(pill.value)}
              className={`shrink-0 rounded-full px-10 py-4 ${
                active ? 'bg-black text-white' : 'bg-light-grey'
              }`}
            >
              {pill.label}
            </button>
          );
        })}
      </div>
      {!debouncedSearchTerm ? (
        isLoading && products?.length === 0 ? (
          <LoadingState />
        ) : products.length > 0 ? (
          <div className="flex flex-col gap-20">
            <div className="flex flex-col gap-8">
              {products.slice(0, 9).map((product, i) => (
                <React.Fragment key={product.id}>
                  <ListingCardList
                    setSmartPricingModalOpen={setSmartPricingModalOpen}
                    product={product}
                    setSelectedProduct={setSelectedProduct}
                    setDeleteListingModalOpen={setDeleteListingModalOpen}
                    setSmartPricingOffModalOpen={setSmartPricingOffModalOpen}
                    setPromoteModalOpen={setPromoteModalIsOpen}
                  />
                  {products.length - 1 > i ? (
                    <div className="h-px w-full bg-[#d9d9d9] lg:hidden" />
                  ) : null}
                </React.Fragment>
              ))}
            </div>

            <PaginatorClient
              currentPage={page}
              onPageChange={(page) => setPage(page)}
              totalPages={data?.pageInfo.totalPages ?? 1}
            />
          </div>
        ) : (
          <EmptyState />
        )
      ) : (
        <InstantSearch searchClient={instantSearchClient} indexName={index}>
          <Configure filters={getFilters()} hitsPerPage={250} />
          <SearchResults
            debouncedSearchTerm={debouncedSearchTerm}
            setSelectedProduct={setSelectedProduct}
            setSmartPricingModalOpen={setSmartPricingModalOpen}
            setDeleteListingModalOpen={setDeleteListingModalOpen}
            setSmartPricingOffModalOpen={setSmartPricingOffModalOpen}
            setPromoteModalOpen={setPromoteModalIsOpen}
            deletedProducts={deletedProducts}
          />
        </InstantSearch>
      )}
    </div>
  );
};

const ListingCardList = ({
  product,
  setSmartPricingModalOpen,
  setSelectedProduct,
  setDeleteListingModalOpen,
  setSmartPricingOffModalOpen,
  setPromoteModalOpen,
}: {
  product: ProductDocument;
  setSmartPricingModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedProduct: React.Dispatch<
    React.SetStateAction<ProductDocument | null>
  >;
  setDeleteListingModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSmartPricingOffModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setPromoteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  // const [now] = useState(() => Date.now());
  // const isStarted = product.start_time! < now;
  // const timeRemaining = useCountdown(
  //   product.start_time!,
  //   product.end_time!,
  //   isStarted ? 'end' : 'start'
  // );

  const { data: views } = useQuery({
    queryKey: ['views', product.id],
    queryFn: () => getProductViews(product.id),
    enabled: !!product.id,
  });

  const queryClient = useQueryClient();
  const { showSuccessToast } = useToastContext();

  const handleSmartPricing = () => {
    setSelectedProduct(product);

    if (product.floor_price) {
      setSmartPricingOffModalOpen(true);
    } else {
      setSmartPricingModalOpen(true);
    }
  };

  const handleDeleteListing = () => {
    setDeleteListingModalOpen(true);
    setSelectedProduct(product);
  };

  const handlePromote = () => {
    setPromoteModalOpen(true);
    setSelectedProduct(product);
  };

  const hasProductStock = !product.integration_info && !product.out_of_stock;

  const formatTimestamp = (timestamp?: number) => {
    if (!timestamp) return '';
    if (timestamp.toString().length === 10) timestamp *= 1000;
    return new Date(timestamp).toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    });
  };

  const getDiscountPercentage = (newPrice: number, ogPrice: number) => {
    return Math.round(((ogPrice - newPrice) * 100) / ogPrice);
  };

  const handleProductBump = async () => {
    await bumpProduct(product.id);
    queryClient.invalidateQueries({
      queryKey: ['listings'],
    });
    showSuccessToast('Successfully bumped listing!');
  };

  return (
    <div>
      <div className="block lg:hidden">
        <div className="mb-6 flex gap-6">
          <Link
            href={getProductSlug(product)}
            className="relative size-40 rounded-3xl"
          >
            <SafeImage
              className="size-full rounded-3xl object-cover"
              width={80}
              height={80}
              alt={product.title + 'image'}
              key={product.thumbnail}
              src={product.thumbnail}
            />
            {/*{product.out_of_stock &&
              !product.is_draft &&
              !product.is_auction && <SoldOverlay variant={'sold'} />}
            {product.is_auction &&
              product.end_time! < now &&
              !product.is_draft &&
              product.out_of_stock && <SoldOverlay variant={'auction'} />}
            {product.is_draft && <DraftOverlay variant="draft" />}*/}
          </Link>
          <div className="flex flex-1 flex-col gap-2">
            <div>
              <span className="text-body-sm font-normal">{product.title}</span>
              <div className="flex items-center gap-3 text-label font-medium text-[#7f7f7f]">
                <span>{product.condition}</span>
                {product.size != undefined ? (
                  <React.Fragment>
                    <div className="size-2 rounded-full bg-[#7f7f7f]" />
                    <span>Size: {String(product.size)}</span>
                  </React.Fragment>
                ) : null}
              </div>
            </div>
            <span className="text-body-md font-semibold">
              {formatCurrency(product.price)}
            </span>
          </div>
          <div>
            <div className="flex items-center gap-2 text-[#7f7f7f]">
              <span className="text-label">{product.favorite_count}</span>
              <Heart className="size-8" />
            </div>
          </div>
        </div>
        <div className="mb-6 flex gap-3 text-body-sm font-semibold">
          {hasProductStock ? (
            <React.Fragment>
              <button
                className="rounded-full bg-black px-6 py-3 text-white"
                onClick={handlePromote}
              >
                Promote
              </button>
              <Link
                href={`/edit-listing/${product.id}`}
                className="rounded-full border border-[#7f7f7f] px-6 py-3"
              >
                Edit listing
              </Link>
            </React.Fragment>
          ) : null}
          <Link
            className="rounded-full border border-[#7f7f7f] px-6 py-3"
            href={{
              pathname: '/create-listing/',
              query: { id: product.id, step: 3 },
            }}
          >
            Duplicate
          </Link>
          <button
            className="rounded-full border border-[#7f7f7f] px-6 py-3"
            onClick={handleDeleteListing}
          >
            Delete
          </button>
          {isVehicleCategory(product.category) && product.bumps > 0 ? (
            <button
              className="rounded-full border border-[#7f7f7f] px-6 py-3"
              onClick={handleProductBump}
            >
              Bump
            </button>
          ) : null}
        </div>

        {!product.integration_info && !product.out_of_stock && (
          <div className="flex h-12 items-center justify-between">
            {Boolean(product.floor_price) ? (
              <span className="rounded-2xl bg-[#f9e8e8] px-4 py-1 text-body-sm font-medium text-brand-secondary">
                Floor Price: {formatCurrency(product.floor_price!)}
              </span>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-2 text-body-sm font-semibold">
              <span className=" text-black">Smart Pricing</span>
              <Toggle
                enabled={Boolean(product.floor_price)}
                onChange={handleSmartPricing}
              />
            </div>
          </div>
        )}
      </div>

      <div className="hidden h-full w-full gap-5 rounded-[24px] bg-white p-4 lg:flex lg:flex-row lg:items-center">
        <div className="flex w-full gap-6">
          <Link href={getProductSlug(product)} className="size-[200px]">
            <SafeImage
              className="size-[200px] rounded-[16px] object-cover"
              width={1920}
              height={1080}
              alt={'product image for ' + product.title}
              key={product.thumbnail}
              src={product.thumbnail}
            />
            {/*{product.out_of_stock &&
              !product.is_draft &&
              !product.is_auction && <SoldOverlay variant={'sold'} />}
            {product.is_auction &&
              product.end_time! < now &&
              !product.is_draft &&
              product.out_of_stock && <SoldOverlay variant={'auction'} />}
            {product.is_draft && <DraftOverlay variant="draft" />}*/}
          </Link>

          <div className="flex-1 flex-col justify-between p-4">
            <div className="flex h-full w-full flex-col justify-between">
              {/*<div className="flex gap-[0.4rem]">
                {product.is_auction && (
                  <div className="w-fit">
                    <Chip text={'Auction'} color={'primary'} />
                  </div>
                )}
                {product.is_auction &&
                  product.end_time! < now &&
                  !product.is_draft &&
                  product.stock === 0 && (
                    <div className="w-fit">
                      <Chip
                        text={`Sold for ${formatCurrency(product.price)}`}
                        color={'success'}
                      />
                    </div>
                  )}
                {product.is_auction &&
                  product.end_time! < now &&
                  !product.is_draft &&
                  product.stock > 0 && (
                    <div className="w-fit">
                      <Chip text={`Didn't sell`} color={'error'} />
                    </div>
                  )}
              </div>*/}

              <div className="flex flex-col gap-6">
                <div className="flex w-full items-start lg:justify-between">
                  <div className="flex flex-col">
                    <span className="text-body-lg font-semibold">
                      {product.title}
                    </span>
                    <div className="flex items-center gap-2 text-body-sm font-medium text-[#7f7f7f]">
                      <span>{product.condition}</span>
                      <div className="size-2 rounded-full bg-[#7f7f7f]" />
                      <span>QTY: {product.stock}</span>
                    </div>
                    {/*
                    <div className="flex items-center gap-x-4">
                      {!!product.size && product.size.letter && (
                        <span className="text-[1.5rem] text-gray-400">
                          Size: {product.size?.letter}
                        </span>
                      )}

                      {!!product.stock && (
                        <span className="text-[1.5rem] text-gray-400">
                          Stock: {product.stock}
                        </span>
                      )}
                    </div>*/}
                  </div>

                  {!product.integration_info && !product.out_of_stock && (
                    <div className="flex flex-col items-end gap-4">
                      <div className="hidden items-center gap-x-6 text-body-sm font-semibold lg:flex">
                        <span className=" text-black">Smart Pricing</span>
                        <Toggle
                          enabled={Boolean(product.floor_price)}
                          onChange={handleSmartPricing}
                        />
                      </div>
                      {Boolean(product.floor_price) ? (
                        <span className="rounded-2xl bg-[#f9e8e8] px-4 py-1 text-body-sm font-medium text-brand-secondary">
                          Floor Price: {formatCurrency(product.floor_price!)}
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-start gap-2">
                  {product.on_sale ? (
                    <div className="rounded-xl bg-brand-secondary px-3 py-1 text-label font-semibold text-white">
                      {getDiscountPercentage(product.price, product.og_price)}%
                      OFF
                    </div>
                  ) : null}
                  <div className="flex items-center text-body-xl font-semibold">
                    {product.on_sale ? (
                      <div className="flex items-center gap-4">
                        <p
                          className={`${
                            product.on_sale
                              ? 'font-bold text-brand-secondary'
                              : 'text-gray-400'
                          }`}
                        >
                          {formatCurrency(product.price)}
                        </p>
                        <p className=" text-[#7f7f7f] line-through">
                          {formatCurrency(product.og_price)}
                        </p>
                      </div>
                    ) : (
                      <p>{formatCurrency(product.price)}</p>
                    )}
                  </div>

                  {!!product.purchase_entry_price && (
                    <span className="rounded-2xl bg-red-50 px-4 py-2 text-blue-500">
                      Purchase Entry Price:{' '}
                      {formatCurrency(product.purchase_entry_price)}
                    </span>
                  )}
                </div>
              </div>
              {/*{product.is_auction &&
                product.end_time! > now &&
                !product.is_draft && (
                  <div className="flex  w-full items-center justify-center gap-[0.8rem] rounded-b-2xl p-2">
                    <span className="w-full font-medium text-brand-green">
                      {isStarted ? 'Ends in ' : 'Starts in '}
                      {timeRemaining.days > 0 && `${timeRemaining.days}d `}
                      {timeRemaining.hours}h {timeRemaining.minutes}m{' '}
                      {timeRemaining.seconds}s
                    </span>
                  </div>
                )}*/}

              <div className="flex items-end justify-between">
                <div className="flex gap-4 text-body-sm font-semibold">
                  {hasProductStock ? (
                    <React.Fragment>
                      <button
                        className="rounded-full bg-black px-6 py-3 text-white"
                        onClick={handlePromote}
                      >
                        Promote
                      </button>
                      <Link
                        href={`/edit-listing/${product.id}`}
                        className="rounded-full border border-[#7f7f7f] px-6 py-3"
                      >
                        Edit listing
                      </Link>
                    </React.Fragment>
                  ) : null}
                  <Link
                    className="rounded-full border border-[#7f7f7f] px-6 py-3"
                    href={{
                      pathname: '/create-listing/',
                      query: { id: product.id, step: 3 },
                    }}
                  >
                    Duplicate
                  </Link>
                  <button
                    className="rounded-full border border-[#7f7f7f] px-6 py-3"
                    onClick={handleDeleteListing}
                  >
                    Delete
                  </button>
                  {isVehicleCategory(product.category) && product.bumps > 0 ? (
                    <button
                      className="rounded-full border border-[#7f7f7f] px-6 py-3"
                      onClick={handleProductBump}
                    >
                      Bump
                    </button>
                  ) : null}
                </div>
                <div className="flex flex-col items-end gap-6 text-body-sm font-medium">
                  {Boolean(product.date_added) ? (
                    <span className="text-[#7f7f7f]">
                      Listed on {formatTimestamp(product.date_added)}
                    </span>
                  ) : null}
                  <div className="flex items-center gap-12 text-body-sm font-medium">
                    {views?.views ? (
                      <span className="flex items-center gap-2">
                        <Eye className=" size-8 text-[#7f7f7f]" />
                        <span>{views.views + 1}</span>
                      </span>
                    ) : null}
                    <span className="flex items-center gap-2">
                      <Heart className="size-8 text-[#7f7f7f]" />
                      <span>{product.favorite_count}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="-ml-24 hidden w-full items-center gap-x-4">
              {!product.integration_info && !product.out_of_stock && (
                <Link
                  href={`/edit-listing/${product.id}`}
                  className="flex lg:hidden"
                >
                  <div className="flex items-center gap-x-2 rounded-xl bg-zinc-200 px-4 py-3 text-[1.5rem] font-semibold text-black">
                    <PencilIcon className={'h-8 w-8'} /> Edit
                  </div>
                </Link>
              )}

              {!product.integration_info && !product.out_of_stock && (
                <div className="flex items-center gap-x-6 lg:hidden">
                  <span className="text-[1.5rem] font-semibold text-black">
                    Smart Pricing
                  </span>

                  <div className="flex items-center gap-x-1">
                    <span
                      className={`pr-2 text-[1.5rem] font-bold ${
                        !!product.floor_price
                          ? 'text-brand-secondary'
                          : 'text-black'
                      }`}
                    >
                      {!!product.floor_price ? 'ON' : 'OFF'}
                    </span>

                    <Toggle
                      enabled={!!product.floor_price}
                      onChange={handleSmartPricing}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function SearchResults({
  debouncedSearchTerm,
  setSelectedProduct,
  setSmartPricingModalOpen,
  setDeleteListingModalOpen,
  setSmartPricingOffModalOpen,
  setPromoteModalOpen,
  deletedProducts,
}: {
  debouncedSearchTerm: string;
  setSelectedProduct: React.Dispatch<
    React.SetStateAction<ProductDocument | null>
  >;
  setSmartPricingModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setDeleteListingModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSmartPricingOffModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setPromoteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  deletedProducts: string[];
}) {
  const { hits } = useHits<ProductDocument>();
  const { refine } = useSearchBox();
  const { status } = useInstantSearch();

  useEffect(() => {
    refine(debouncedSearchTerm);
  }, [debouncedSearchTerm, refine]);

  const filteredHits = hits.filter(
    (hit) => !deletedProducts.some((id) => id === hit.id)
  );

  const isLoading = status === 'loading' || status === 'stalled';
  return (
    <>
      {isLoading ? (
        <LoadingState />
      ) : hits.length > 0 ? (
        <div className="flex flex-col gap-8">
          {filteredHits.map((product, i) => (
            <React.Fragment key={product.id}>
              <ListingCardList
                product={product}
                setSelectedProduct={setSelectedProduct}
                setSmartPricingModalOpen={setSmartPricingModalOpen}
                setDeleteListingModalOpen={setDeleteListingModalOpen}
                setSmartPricingOffModalOpen={setSmartPricingOffModalOpen}
                setPromoteModalOpen={setPromoteModalOpen}
              />
              {filteredHits.length - 1 > i ? (
                <div className="h-px w-full bg-[#d9d9d9]" />
              ) : null}
            </React.Fragment>
          ))}
        </div>
      ) : (
        <EmptyState />
      )}
    </>
  );
}

export const SoldOverlay = ({ variant }: { variant: 'auction' | 'sold' }) => (
  <div className="pointer-events-none absolute top-0 flex h-full w-full items-center justify-center @container">
    <div className=" flex w-full flex-col items-center justify-center bg-brand-secondary py-[0.8rem] font-semibold text-white opacity-80">
      {variant === 'auction' ? 'ENDED' : 'SOLD'}
    </div>
  </div>
);

export const DraftOverlay = ({ variant }: { variant: 'auction' | 'draft' }) => (
  <div className="absolute left-0 top-0 h-full w-full rounded-2xl bg-brand-lightest-black bg-opacity-50">
    <div className="absolute left-0 top-0 flex h-full w-full flex-col items-center justify-center">
      <div
        className={`w-full ${
          variant === 'auction' ? 'bg-orange-500' : 'bg-yellow-500'
        } px-[1.6rem] py-[1.4rem] text-center font-semibold text-brand-white opacity-50`}
      >
        {variant === 'auction' ? 'Auction Starting Soon' : 'Draft'}
      </div>
    </div>
  </div>
);

export const AnotherPersonCartOverlay = ({ count }: { count: number }) => (
  <div className="absolute left-6 mt-6 h-[3rem] rounded-full bg-brand-secondary px-4">
    <div className="flex h-full items-center justify-center">
      <div className="whitespace-nowrap text-center text-[1.4rem] font-semibold uppercase text-brand-white sm:text-[1.5rem]">
        {count} {count === 1 ? 'person has' : 'people have'} this in their cart
      </div>
    </div>
  </div>
);

const EmptyState = () => {
  return (
    <div className="mt-4 flex h-full w-full grow flex-col items-center justify-center py-32">
      <div className="flex flex-col items-center gap-8">
        <SafeImage src={EmptyBox} alt="empty box" width={100} height={100} />
        <h1 className="text-heading-h4 font-bold">No listings yet</h1>

        <span className="text-body-sm font-medium text-text-muted">
          Add your first item and start earning cash
        </span>

        <Link
          href={'/create-listing'}
          className="translation-all flex cursor-pointer items-center gap-4 rounded-full border-none bg-brand-secondary px-10 py-5 text-body-md font-semibold text-white duration-150 hover:bg-brand-secondary-darker"
        >
          <PlusIcon className="size-10" />
          Add listing
        </Link>
      </div>
    </div>
  );
};

const LoadingState = () => {
  return (
    <div className="mt-4 flex h-full w-full grow flex-col items-center justify-center">
      <div className="flex w-full animate-pulse flex-col gap-[3.2rem]">
        {[1, 2, 3, 4].map((i) => (
          <div className="flex w-full flex-col gap-2" key={i}>
            <div className="h-4 w-full rounded bg-gray-300"></div>
            <div className="h-4 w-1/2 rounded bg-gray-300"></div>
          </div>
        ))}
      </div>
    </div>
  );
};
